import { type HTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Card variant
   * @default 'default'
   */
  variant?: 'default' | 'bordered' | 'elevated';

  /**
   * Padding size
   * @default 'md'
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * Whether the card is hoverable (adds hover effect)
   * @default false
   */
  hoverable?: boolean;

  /**
   * Whether the card is clickable (adds cursor pointer)
   * @default false
   */
  clickable?: boolean;
}

/**
 * Card component
 *
 * A versatile container component for grouping related content.
 * Supports different variants, padding sizes, and interactive states.
 *
 * @example
 * ```tsx
 * <Card>
 *   <h3>Card Title</h3>
 *   <p>Card content goes here</p>
 * </Card>
 *
 * <Card variant="elevated" hoverable>
 *   <h3>Hoverable Card</h3>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hoverable = false,
      clickable = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = ['rounded-lg', 'transition-all duration-200'];

    const variantStyles = {
      default: ['bg-white dark:bg-gray-800', 'border border-gray-200 dark:border-gray-700'],
      bordered: ['bg-white dark:bg-gray-800', 'border-2 border-gray-300 dark:border-gray-600'],
      elevated: ['bg-white dark:bg-gray-800', 'shadow-md dark:shadow-gray-900/50'],
    };

    const paddingStyles = {
      none: '',
      sm: 'p-3',
      md: 'p-6',
      lg: 'p-8',
    };

    const hoverStyles = hoverable
      ? variant === 'elevated'
        ? 'hover:shadow-lg hover:-translate-y-0.5'
        : 'hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-sm'
      : '';

    const clickableStyles = clickable
      ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900'
      : '';

    return (
      <div
        ref={ref}
        className={cn(
          baseStyles,
          variantStyles[variant],
          paddingStyles[padding],
          hoverStyles,
          clickableStyles,
          className
        )}
        tabIndex={clickable ? 0 : undefined}
        role={clickable ? 'button' : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to add a bottom border
   * @default true
   */
  divider?: boolean;
}

/**
 * CardHeader component
 *
 * Header section for a Card, typically contains a title and optional actions.
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ divider = true, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'mb-4',
          divider && 'pb-4 border-b border-gray-200 dark:border-gray-700',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /**
   * HTML heading level
   * @default 'h3'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * CardTitle component
 *
 * Title for a Card, renders as a heading element.
 */
export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ as: Component = 'h3', className, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn('text-lg font-semibold text-gray-900 dark:text-gray-100', className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = 'CardTitle';

export interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

/**
 * CardDescription component
 *
 * Description text for a Card.
 */
export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-gray-500 dark:text-gray-400 mt-1', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * CardContent component
 *
 * Main content area of a Card.
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to add a top border
   * @default true
   */
  divider?: boolean;
}

/**
 * CardFooter component
 *
 * Footer section for a Card, typically contains actions or metadata.
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ divider = true, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'mt-4',
          divider && 'pt-4 border-t border-gray-200 dark:border-gray-700',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';
